import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Mail, KeyRound, Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || fallback;
  return fallback;
}

const TITLES = {
  login: "Login to ComplaintAdvisor",
  signup: "Create your account",
  verify: "Verify your email",
  forgot: "Reset your password",
  reset: "Set a new password",
};

export default function AuthModal() {
  const { authOpen, setAuthOpen, authMode, login, setShowPartyPick } = useAuth();
  const [view, setView] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [otp, setOtp] = useState("");
  const [userType, setUserType] = useState("customer");
  const [terms, setTerms] = useState(false);
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authOpen) {
      setView(authMode === "signup" ? "signup" : "login");
      setPassword(""); setConfirm(""); setOtp(""); setDemoOtp(""); setTerms(false);
    }
  }, [authOpen, authMode]);

  const reset = () => {
    setName(""); setEmail(""); setPassword(""); setConfirm(""); setOtp("");
    setDemoOtp(""); setTerms(false); setView("login");
  };

  const doLogin = async () => {
    if (!email || !password) return toast.error("Enter your email and password");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      login(res.data.token, res.data.user);
      toast.success(`Welcome back, ${res.data.user.name || "user"}!`);
      reset();
    } catch (e) {
      if (e?.response?.status === 403) {
        setView("signup");
        toast.error("Please verify your email. Sign up again to receive a new code.");
      } else {
        toast.error(errText(e, "Login failed"));
      }
    }
    setLoading(false);
  };

  const startSignup = async () => {
    if (!name.trim()) return toast.error("Enter your name");
    if (!email) return toast.error("Enter your email");
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    if (!terms) return toast.error("Please accept the policies to continue");
    setLoading(true);
    try {
      const res = await api.post("/auth/register/start", {
        name, email, password, user_type: userType, accepted_terms: terms,
      });
      setDemoOtp(res.data.demo_otp || "");
      setView("verify");
      toast.success("Verification code sent to your email");
    } catch (e) { toast.error(errText(e, "Could not start signup")); }
    setLoading(false);
  };

  const verifySignup = async () => {
    if (otp.trim().length < 6) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      const res = await api.post("/auth/register/verify", { email, otp });
      login(res.data.token, res.data.user);
      toast.success(`Welcome, ${res.data.user.name || "user"}!`);
      reset();
      setShowPartyPick(true);
    } catch (e) { toast.error(errText(e, "Verification failed")); }
    setLoading(false);
  };

  const resend = async () => {
    try {
      const res = await api.post("/auth/resend-otp", { email });
      setDemoOtp(res.data.demo_otp || "");
      toast.success("A new code has been sent");
    } catch (e) { toast.error(errText(e, "Could not resend code")); }
  };

  const startForgot = async () => {
    if (!email) return toast.error("Enter your email");
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setDemoOtp(res.data.demo_otp || "");
      setView("reset");
      toast.success("If an account exists, a reset code has been sent");
    } catch (e) { toast.error(errText(e, "Could not send reset code")); }
    setLoading(false);
  };

  const doReset = async () => {
    if (otp.trim().length < 6) return toast.error("Enter the 6-digit code");
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: password });
      toast.success("Password reset. Please log in.");
      setPassword(""); setConfirm(""); setOtp(""); setDemoOtp(""); setView("login");
    } catch (e) { toast.error(errText(e, "Reset failed")); }
    setLoading(false);
  };

  return (
    <Dialog open={authOpen} onOpenChange={(v) => { setAuthOpen(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-teal-brand/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-teal" />
            </div>
            <DialogTitle className="font-display text-xl">{TITLES[view]}</DialogTitle>
          </div>
        </DialogHeader>

        {view === "login" && (
          <div className="space-y-4 pt-2">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="Your password"
                onKeyDown={(e) => e.key === "Enter" && doLogin()} />
            </div>
            <button className="text-xs text-teal hover:underline" onClick={() => setView("forgot")}
              data-testid="login-forgot-password-link">Forgot password?</button>
            <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" disabled={loading}
              onClick={doLogin} data-testid="login-submit-button">
              <Lock className="h-4 w-4 mr-2" /> {loading ? "Signing in…" : "Login"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              New here?{" "}
              <button className="text-teal font-medium hover:underline" onClick={() => setView("signup")}
                data-testid="login-register-link">Create an account</button>
            </p>
          </div>
        )}

        {view === "signup" && (
          <div className="space-y-4 pt-2">
            <div>
              <Label>Full name / Display name</Label>
              <Input data-testid="register-name-input" value={name}
                onChange={(e) => setName(e.target.value)} placeholder="e.g. Rahul Sharma" />
            </div>
            <div>
              <Label>Email</Label>
              <Input data-testid="register-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="register-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
            </div>
            <div>
              <Label>Confirm password</Label>
              <Input data-testid="register-password-confirm-input" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
            </div>
            <div>
              <Label>I am a</Label>
              <Select value={userType} onValueChange={setUserType}>
                <SelectTrigger data-testid="auth-usertype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="both">Both Customer & Vendor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-600">
              <Checkbox checked={terms} onCheckedChange={setTerms} data-testid="auth-terms-check" className="mt-0.5" />
              <span>I accept the Terms of Use, Privacy Policy, Complaint &amp; Evidence Policy and Community Conduct Policy.</span>
            </label>
            <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" disabled={loading}
              onClick={startSignup} data-testid="register-submit-button">
              <Mail className="h-4 w-4 mr-2" /> {loading ? "Sending…" : "Create account"}
            </Button>
            <p className="text-xs text-slate-500 text-center">
              Already have an account?{" "}
              <button className="text-teal font-medium hover:underline" onClick={() => setView("login")}
                data-testid="register-login-link">Login</button>
            </p>
          </div>
        )}

        {view === "verify" && (
          <div className="space-y-4 pt-2">
            {demoOtp && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="demo-otp-box">
                Demo code (testing only): <span className="font-mono font-bold">{demoOtp}</span>
              </div>
            )}
            <div>
              <Label>Enter the 6-digit code sent to {email}</Label>
              <Input data-testid="verify-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)}
                maxLength={6} placeholder="______" className="tracking-[0.5em] text-center font-mono text-lg" />
            </div>
            <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" disabled={loading}
              onClick={verifySignup} data-testid="verify-submit-button">
              <KeyRound className="h-4 w-4 mr-2" /> {loading ? "Verifying…" : "Verify & Continue"}
            </Button>
            <div className="flex items-center justify-between text-xs">
              <button className="text-slate-500 hover:text-teal inline-flex items-center gap-1"
                onClick={() => setView("signup")}><ArrowLeft className="h-3 w-3" /> Back</button>
              <button className="text-teal hover:underline" onClick={resend} data-testid="verify-resend-btn">Resend code</button>
            </div>
          </div>
        )}

        {view === "forgot" && (
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">Enter your account email. We'll send a 6-digit code to reset your password.</p>
            <div>
              <Label>Email</Label>
              <Input data-testid="forgot-email-input" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" disabled={loading}
              onClick={startForgot} data-testid="forgot-submit-button">
              <Mail className="h-4 w-4 mr-2" /> {loading ? "Sending…" : "Send reset code"}
            </Button>
            <button className="text-xs text-slate-500 hover:text-teal inline-flex items-center gap-1"
              onClick={() => setView("login")}><ArrowLeft className="h-3 w-3" /> Back to login</button>
          </div>
        )}

        {view === "reset" && (
          <div className="space-y-4 pt-2">
            {demoOtp && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="demo-otp-box">
                Demo code (testing only): <span className="font-mono font-bold">{demoOtp}</span>
              </div>
            )}
            <div>
              <Label>6-digit code sent to {email}</Label>
              <Input data-testid="reset-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)}
                maxLength={6} placeholder="______" className="tracking-[0.4em] text-center font-mono" />
            </div>
            <div>
              <Label>New password</Label>
              <Input data-testid="reset-password-input" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input data-testid="reset-password-confirm-input" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
            </div>
            <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" disabled={loading}
              onClick={doReset} data-testid="reset-submit-button">
              <KeyRound className="h-4 w-4 mr-2" /> {loading ? "Resetting…" : "Reset password"}
            </Button>
            <button className="text-xs text-slate-500 hover:text-teal inline-flex items-center gap-1"
              onClick={() => setView("login")}><ArrowLeft className="h-3 w-3" /> Back to login</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
